/**
 * Cross-platform helper to open an external verification URL.
 *
 * - web: window.open in a new tab, without giving that page `window.opener`
 * - desktop: IPC to open a child BrowserWindow
 * - mobile: @capacitor/browser (native in-app browser)
 *
 * Each open returns a handle that resolves once when that window closes, so
 * the app can check the provider status immediately instead of waiting out
 * the poll backoff.
 */

import type { Platform } from '@adieuu/shared';

interface ElectronBridge {
  invoke?: (channel: string, ...args: unknown[]) => Promise<unknown>;
  on?: (channel: string, cb: (...args: unknown[]) => void) => () => void;
}

interface CapacitorBrowser {
  open(options: { url: string }): Promise<void>;
  addListener?(
    event: 'browserFinished',
    listener: () => void,
  ): Promise<{ remove: () => Promise<void> | void }>;
}

/** A window we can watch without retaining a scriptable opener. */
export interface CloseableWindow {
  closed: boolean;
  opener?: unknown;
}

export interface VerificationWindowHandle {
  /** Resolves once, when the hosted window closes. Never resolves if we cannot tell. */
  closed: Promise<void>;
  /** Drops the watcher. A later close does not resolve `closed`. */
  stop: () => void;
}

const CLOSED_POLL_MS = 400;

function neverCloses(): VerificationWindowHandle {
  return { closed: new Promise(() => {}), stop: () => {} };
}

/**
 * Resolves the first time `opened.closed` is true, and only once.
 * A null window (popup blocked, or no handle) never resolves.
 */
export function watchBrowserWindowClosed(
  opened: CloseableWindow | null,
  intervalMs = CLOSED_POLL_MS,
): VerificationWindowHandle {
  if (!opened) return neverCloses();

  let timer: ReturnType<typeof setInterval> | undefined;
  let settled = false;

  const closed = new Promise<void>((resolve) => {
    const finish = () => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearInterval(timer);
      resolve();
    };

    try {
      opened.opener = null;
    } catch {
      // The page may already be cross-origin.
    }

    if (opened.closed) {
      finish();
      return;
    }

    timer = setInterval(() => {
      if (opened.closed) finish();
    }, intervalMs);
  });

  return {
    closed,
    stop: () => {
      settled = true;
      if (timer !== undefined) clearInterval(timer);
    },
  };
}

/** Asset id from an Agemin hosted URL (`/start/{assetId}`). Absent for other providers. */
export function appIdFromVerificationUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const match = new URL(url).pathname.match(/\/start\/([^/]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : undefined;
  } catch {
    return undefined;
  }
}

function openOnWeb(url: string): VerificationWindowHandle {
  // No `noopener` feature: that makes `window.open` return null, so we could
  // not see the tab close. Clearing `opener` afterwards keeps AgeMin from
  // scripting this page.
  const opened = window.open(url, '_blank');
  return watchBrowserWindowClosed(opened);
}

function electronBridge(): ElectronBridge | undefined {
  return (window as { electron?: ElectronBridge }).electron;
}

export async function openVerificationUrl(
  url: string,
  platform: Platform,
): Promise<VerificationWindowHandle> {
  switch (platform) {
    case 'desktop': {
      const electron = electronBridge();
      if (electron?.invoke && electron.on) {
        let stop = () => {};
        const closed = new Promise<void>((resolve) => {
          const unsubscribe = electron.on!('verification-window-closed', () => {
            unsubscribe();
            resolve();
          });
          stop = () => {
            unsubscribe();
          };
        });
        try {
          await electron.invoke('open-verification-window', url);
        } catch {
          stop();
          return openOnWeb(url);
        }
        return { closed, stop };
      }
      return openOnWeb(url);
    }

    case 'mobile': {
      try {
        const capacitorModule = '@capacitor/browser';
        const mod = (await import(/* @vite-ignore */ capacitorModule)) as {
          Browser: CapacitorBrowser;
        };
        if (!mod.Browser.addListener) {
          await mod.Browser.open({ url });
          return openOnWeb(url);
        }
        let remove = () => {};
        const closed = new Promise<void>((resolve) => {
          void mod.Browser.addListener!('browserFinished', () => {
            remove();
            resolve();
          }).then((handle) => {
            remove = () => {
              void handle.remove();
            };
          });
        });
        await mod.Browser.open({ url });
        return {
          closed,
          stop: () => {
            remove();
          },
        };
      } catch {
        return openOnWeb(url);
      }
    }

    default:
      return openOnWeb(url);
  }
}
